// NEVANU — Offline-Datenbank (IndexedDB via Dexie.js)
// Speichert Termine, Leistungen und Team für Offline-Zugriff.
// Sync-Queue für Mutationen die offline gemacht werden.
//
// B12 (Strang 2, 09.10.2026): «src/offline/db.ts: Offline-Kopie nur Termine des Tages und der Woche, mit Ablauf, beim
// Abmelden gelöscht.»
//   - KEINE Kundenliste mehr auf dem Gerät. Bis hier lud syncKunden() bei jedem Start ALLE Kundinnen samt Mail, Telefon,
//     Adresse, Geburtstag und Notizen in den Browser — gelesen hat sie keine Stelle der App. Version 2 der Datenbank
//     löscht die Tabelle auf jedem Gerät beim nächsten Start.
//   - Termine nur für heute und die nächsten 6 Tage (OFFLINE_TAGE = 7); was ausserhalb liegt, wird gelöscht.
//   - Ablauf: jeder Eintrag trägt synced_at; älter als OFFLINE_ABLAUF_MS (24 h) → gelöscht (offlineAufraeumen()).
//   - Abmelden löscht alles (offlineLoeschen()) — aus clearSession() (src/core/auth.ts) und logoutPin() (js/form.js).
//   - B6 (Teil): keine PIN und kein Name mehr in den Abruf-Adressen — api.php meldet über das Ticket-Cookie an.

import Dexie, { type Table } from 'dexie';

/** Offline-Termin */
export interface OfflineTermin {
  id: number;
  kunde_id?: number;
  kunde_name?: string;
  stylistin: string;
  datum: string;
  zeit_von: string;
  zeit_bis: string;
  leistungen?: string;
  preis?: number;
  status?: string;
  notizen?: string;
  synced_at?: string;
}

/** Offline-Leistung */
export interface OfflineLeistung {
  id: number;
  kategorie: string;
  paket: string;
  preis: number;
  dauer?: number;
  active?: boolean;
  synced_at?: string;
}

/** Team-Mitglied */
export interface OfflineTeamMember {
  id: number;
  name: string;
  role: string;
  email?: string;
  avatar?: string;
  farbe?: string;
  active: boolean;
  synced_at?: string;
}

/** Sync-Queue Eintrag (für offline Mutationen) */
export interface SyncQueueItem {
  id?: number;
  route: string;        // z.B. 'kunden', 'buchungen'
  action: string;       // z.B. 'save', 'delete'
  payload: string;      // JSON-String der Daten
  created_at: string;
  status: 'pending' | 'syncing' | 'failed';
  retries: number;
  error?: string;
}

/** B12: Termine nur für heute und die nächsten 6 Tage */
export const OFFLINE_TAGE = 7;
/** B12: ein Eintrag gilt 24 Stunden nach dem Abgleich, danach wird er gelöscht */
export const OFFLINE_ABLAUF_MS = 24 * 60 * 60 * 1000;

/** NEVANU Offline-Datenbank */
class NevanuDB extends Dexie {
  termine!: Table<OfflineTermin, number>;
  leistungen!: Table<OfflineLeistung, number>;
  team!: Table<OfflineTeamMember, number>;
  syncQueue!: Table<SyncQueueItem, number>;

  constructor() {
    super('NevanuOffline');

    this.version(1).stores({
      kunden: 'id, name, email, telefon',
      termine: 'id, datum, stylistin, kunde_id, [datum+stylistin]',
      leistungen: 'id, kategorie, active',
      team: 'id, name, role',
      syncQueue: '++id, route, status, created_at',
    });
    // B12: die Kundenliste verlässt das Gerät — Version 2 löscht die Tabelle (Dexie: null = Tabelle entfernen)
    this.version(2).stores({
      kunden: null,
      termine: 'id, datum, stylistin, kunde_id, [datum+stylistin], synced_at',
      team: 'id, name, role, synced_at',
      leistungen: 'id, kategorie, active, synced_at',
    });
  }
}

/** Singleton-Instanz */
export const db = new NevanuDB();

/** Session-Daten für Legacy-API-Calls (PIN + User) */

/** B12: YYYY-MM-DD in Ortszeit (nicht UTC — sonst verschiebt sich «heute» nach 22/23 Uhr) */
function tagIso(d: Date): string {
  const z = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + z(d.getMonth() + 1) + '-' + z(d.getDate());
}

/** B12: das erlaubte Fenster — heute bis heute + OFFLINE_TAGE − 1 */
export function offlineFenster(jetzt: Date = new Date()): { von: string; bis: string } {
  const ende = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate() + OFFLINE_TAGE - 1);
  return { von: tagIso(jetzt), bis: tagIso(ende) };
}

/**
 * B12: Ablauf — löscht Termine ausserhalb des Fensters und jeden Eintrag, dessen Abgleich älter als OFFLINE_ABLAUF_MS ist.
 * Gibt die Zahl der gelöschten Einträge zurück.
 */
export async function offlineAufraeumen(jetzt: Date = new Date()): Promise<number> {
  const f = offlineFenster(jetzt);
  const grenze = new Date(jetzt.getTime() - OFFLINE_ABLAUF_MS).toISOString();
  let n = 0;
  try {
    n += await db.termine.where('datum').below(f.von).delete();
    n += await db.termine.where('datum').above(f.bis).delete();
    n += await db.termine.filter(t => !t.synced_at || t.synced_at < grenze).delete();
    n += await db.leistungen.filter(l => !l.synced_at || l.synced_at < grenze).delete();
    n += await db.team.filter(t => !t.synced_at || t.synced_at < grenze).delete();
  } catch (e) {
    console.warn('[Offline] Aufräumen fehlgeschlagen:', e);
  }
  return n;
}

/**
 * B12: Abmelden — alles vom Gerät. Ausstehende Änderungen werden vorher noch gesendet, wenn das Netz da ist; sonst
 * gehen sie verloren (sie gehören der abgemeldeten Person und dürfen nicht unter der nächsten Anmeldung gesendet werden).
 */
export async function offlineLoeschen(): Promise<void> {
  try {
    if (navigator.onLine) await processSyncQueue();
  } catch { /* weiter: gelöscht wird trotzdem */ }
  try {
    await Promise.all([db.termine.clear(), db.leistungen.clear(), db.team.clear(), db.syncQueue.clear()]);
    console.log('[Offline] Abgemeldet — Offline-Kopie gelöscht');
  } catch (e) {
    console.warn('[Offline] Löschen fehlgeschlagen:', e);
  }
}

/**
 * Termine für einen Zeitraum synchronisieren
 */
export async function syncTermine(von?: string, bis?: string): Promise<number> {
  try {
    // B12: nur heute und die nächsten 6 Tage — ein weiter gefasster Wunsch wird auf das Fenster gekürzt
    const f = offlineFenster();
    const start = von && von > f.von ? von : f.von;
    const end = bis && bis < f.bis ? bis : f.bis;

    // API nutzt 'kalender' Action (nicht 'range') mit GET
    const response = await fetch(`/api.php?route=buchungen&action=kalender&von=${start}&bis=${end}`, {
      credentials: 'include',
    });
    const data = await response.json();
    if (data.ok && data.termine) {
      const now = new Date().toISOString();
      const termineWithSync = data.termine.map((t: any) => ({
        id: t.id,
        kunde_id: t.kunden_id || t.kunde_id,
        kunde_name: t.kunde_name || '',
        stylistin: t.team_name || '',
        datum: t.datum,
        zeit_von: t.start_zeit || t.zeit || '',
        zeit_bis: t.end_zeit || '',
        leistungen: t.leistung_name || t.leistung || '',
        preis: t.preis ? parseFloat(t.preis) : 0,
        status: t.status || '',
        notizen: t.notizen || '',
        synced_at: now,
      }));
      // Nur diesen Zeitraum ersetzen (nicht alle löschen!)
      await db.termine.where('datum').between(start, end).delete();
      await db.termine.bulkPut(termineWithSync);
      console.log(`[Offline] ${termineWithSync.length} Termine synchronisiert (${start} – ${end})`);
      return termineWithSync.length;
    }
  } catch (e) {
    console.warn('[Offline] Termine-Sync fehlgeschlagen:', e);
  }
  return 0;
}

/**
 * Leistungen synchronisieren
 */
export async function syncLeistungen(): Promise<number> {
  try {
    const response = await fetch(`/api.php?route=leistungen&action=list`, {
      credentials: 'include',
    });
    const data = await response.json();
    if (data.ok && data.leistungen) {
      const now = new Date().toISOString();
      const items = data.leistungen.map((l: any) => ({ ...l, synced_at: now }));
      await db.leistungen.clear();
      await db.leistungen.bulkPut(items);
      console.log(`[Offline] ${items.length} Leistungen synchronisiert`);
      return items.length;
    }
  } catch (e) {
    console.warn('[Offline] Leistungen-Sync fehlgeschlagen:', e);
  }
  return 0;
}

/**
 * Team synchronisieren
 */
export async function syncTeam(): Promise<number> {
  try {
    const response = await fetch(`/api.php?route=team&action=list`, {
      credentials: 'include',
    });
    const data = await response.json();
    if (data.ok && data.team) {
      const now = new Date().toISOString();
      const items = data.team.map((t: any) => ({
        id: t.id,
        name: t.name,
        role: t.role,
        email: t.email,
        avatar: t.avatar_url || null,
        farbe: t.farbe || null,
        active: true,
        synced_at: now,
      }));
      await db.team.clear();
      await db.team.bulkPut(items);
      console.log(`[Offline] ${items.length} Team-Mitglieder synchronisiert`);
      return items.length;
    }
  } catch (e) {
    console.warn('[Offline] Team-Sync fehlgeschlagen:', e);
  }
  return 0;
}

/**
 * Alle Daten synchronisieren (bei App-Start oder wenn online)
 */
export async function syncAll(): Promise<void> {
  // B12: zuerst der Ablauf — auch ohne Netz verschwindet, was abgelaufen ist
  await offlineAufraeumen();
  if (!navigator.onLine) {
    console.log('[Offline] Kein Netzwerk — Sync übersprungen');
    return;
  }

  console.log('[Offline] Starte Synchronisierung...');
  const results = await Promise.allSettled([
    syncTermine(),
    syncLeistungen(),
    syncTeam(),
  ]);

  const counts = results.map(r => r.status === 'fulfilled' ? r.value : 0);
  console.log(`[Offline] Sync fertig: ${counts[0]} Termine, ${counts[1]} Leistungen, ${counts[2]} Team`);

  // Pending Sync-Queue verarbeiten
  await processSyncQueue();
}

/**
 * Mutation zur Sync-Queue hinzufügen (für offline Änderungen)
 */
export async function addToSyncQueue(route: string, action: string, payload: any): Promise<void> {
  await db.syncQueue.add({
    route,
    action,
    payload: JSON.stringify(payload),
    created_at: new Date().toISOString(),
    status: 'pending',
    retries: 0,
  });
  console.log(`[Offline] Mutation in Queue: ${route}/${action}`);
}

/**
 * Sync-Queue verarbeiten (wenn online)
 */
export async function processSyncQueue(): Promise<void> {
  if (!navigator.onLine) return;

  const pending = await db.syncQueue.where('status').equals('pending').toArray();
  if (pending.length === 0) return;

  console.log(`[Offline] ${pending.length} ausstehende Mutationen verarbeiten...`);

  for (const item of pending) {
    try {
      await db.syncQueue.update(item.id!, { status: 'syncing' });

      const payload = JSON.parse(item.payload);
      // B1 (Strang 2, 08.10.2026): ueber api.php; schreibend → CSRF-Kopfzeile aus dem Cookie XSRF-TOKEN (api/security.php)
      const xsrf = (document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/) || [])[1];
      const response = await fetch(`/api.php?route=${encodeURIComponent(item.route)}&action=${encodeURIComponent(item.action)}`, {
        method: 'POST',
        credentials: 'include',
        headers: Object.assign({ 'Content-Type': 'application/json' }, xsrf ? { 'X-CSRF-Token': decodeURIComponent(xsrf) } : {}),
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.ok) {
        await db.syncQueue.delete(item.id!);
        console.log(`[Offline] ✓ Sync: ${item.route}/${item.action}`);
      } else {
        throw new Error(data.error || 'Server-Fehler');
      }
    } catch (e: any) {
      const retries = (item.retries || 0) + 1;
      await db.syncQueue.update(item.id!, {
        status: retries >= 3 ? 'failed' : 'pending',
        retries,
        error: e.message,
      });
      console.warn(`[Offline] ✗ Sync fehlgeschlagen: ${item.route}/${item.action} (Versuch ${retries})`);
    }
  }
}

/**
 * Offline-Termine für ein Datum abrufen
 */
export async function getOfflineTermine(datum: string, stylistin?: string): Promise<OfflineTermin[]> {
  await offlineAufraeumen();   // B12: nie einen abgelaufenen Termin zeigen
  let query = db.termine.where('datum').equals(datum);
  const termine = await query.toArray();
  if (stylistin) {
    return termine.filter(t => t.stylistin === stylistin);
  }
  return termine.sort((a, b) => a.zeit_von.localeCompare(b.zeit_von));
}

/**
 * Letzte Sync-Zeit holen
 */
export async function getLastSyncTime(): Promise<string | null> {
  const termin = await db.termine.orderBy('synced_at').last();
  return termin?.synced_at || null;
}
